import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, Form, Button, Row, Col, Alert } from "react-bootstrap";
import Spinner from "../generics/Spinner";

const MAINTENANCE_TYPES = [
  { value: "SCHEDULED", label: "Scheduled Maintenance" },
  { value: "REPAIR", label: "Repair" },
  { value: "INSPECTION", label: "Inspection" },
  { value: "CLEANING", label: "Cleaning" },
  { value: "REPLACEMENT", label: "Part Replacement" },
  { value: "EMERGENCY", label: "Emergency Repair" },
  { value: "OTHER", label: "Other" },
];

const RECORD_STATUSES = [
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

const MaintenanceRecordForm = ({
  record,
  machines,
  serviceProviders,
  loading,
  error,
  onSubmit,
  isEdit = false,
  isServiceVisit = false,
}) => {
  const [searchParams] = useSearchParams();
  const preselectedMachine = searchParams.get("machine");

  const [formData, setFormData] = useState({
    machine: preselectedMachine || "",
    maintenance_type: isServiceVisit ? "REPAIR" : "SCHEDULED",
    title: "",
    description: "",
    service_provider: "",
    scheduled_date: "",
    status: isServiceVisit ? "COMPLETED" : "SCHEDULED",
    cost: "",
    parts_replaced: "",
    findings: "",
    recommendations: "",
    external_technician_name: "",
  });
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (record && isEdit) {
      setFormData({
        machine: record.machine || "",
        maintenance_type: record.maintenance_type || "SCHEDULED",
        title: record.title || "",
        description: record.description || "",
        service_provider: record.service_provider || "",
        scheduled_date: record.scheduled_date || "",
        status: record.status || "SCHEDULED",
        cost: record.cost || "",
        parts_replaced: record.parts_replaced || "",
        findings: record.findings || "",
        recommendations: record.recommendations || "",
        external_technician_name: record.external_technician_name || "",
      });
    }
  }, [record, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    // Clean up empty fields
    const submitData = {};
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== "" && formData[key] !== null) {
        submitData[key] = formData[key];
      }
    });

    // record_service_visit endpoint expects machine_id/service_provider_id
    if (isServiceVisit) {
      if (submitData.machine) {
        submitData.machine_id = submitData.machine;
        delete submitData.machine;
      }
      if (submitData.service_provider) {
        submitData.service_provider_id = submitData.service_provider;
        delete submitData.service_provider;
      }
      // Remove fields not applicable to service visit
      delete submitData.status;
      delete submitData.scheduled_date;
    }

    if (onSubmit) {
      onSubmit(submitData, isServiceVisit);
    }
  };

  if (loading && isEdit && !record) {
    return <Spinner />;
  }

  const pageTitle = isServiceVisit
    ? "Record Service Visit"
    : isEdit
    ? "Edit Maintenance Record"
    : "Schedule Maintenance";

  return (
    <div className="maintenance-record-form">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className={`fa ${isServiceVisit ? "fa-clipboard" : "fa-calendar"} me-2`}></i>
          {pageTitle}
        </h2>
        <Link to="/maintenance/records" className="btn btn-outline-secondary">
          <i className="fa fa-arrow-left me-1"></i> Back
        </Link>
      </div>

      {isServiceVisit && (
        <Alert variant="info" className="mb-4">
          <i className="fa fa-info-circle me-2"></i>
          Use this form to quickly record a completed service visit from an external provider.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mb-4">
          <i className="fa fa-exclamation-circle me-2"></i>
          {typeof error === "object"
            ? Object.entries(error).map(([key, val]) => (
                <div key={key}><strong>{key}:</strong> {Array.isArray(val) ? val.join(", ") : val}</div>
              ))
            : error.message || "An error occurred"}
        </Alert>
      )}

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Row>
          <Col md={8}>
            <Card className="mb-4">
              <Card.Header>
                <i className="fa fa-info-circle me-2"></i>
                Basic Information
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Machine *</Form.Label>
                      <Form.Select
                        name="machine"
                        value={formData.machine}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Machine</option>
                        {machines.map((machine) => (
                          <option key={machine.id} value={machine.id}>
                            {machine.name} ({machine.location_name})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Machine is required
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Maintenance Type *</Form.Label>
                      <Form.Select
                        name="maintenance_type"
                        value={formData.maintenance_type}
                        onChange={handleChange}
                        required
                      >
                        {MAINTENANCE_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Title *</Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        placeholder="e.g., Quarterly Filter Replacement"
                      />
                      <Form.Control.Feedback type="invalid">
                        Title is required
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Describe the maintenance work to be performed"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Service Provider</Form.Label>
                      <Form.Select
                        name="service_provider"
                        value={formData.service_provider}
                        onChange={handleChange}
                      >
                        <option value="">Select Provider (optional)</option>
                        {serviceProviders.map((provider) => (
                          <option key={provider.id} value={provider.id}>
                            {provider.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Technician Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="external_technician_name"
                        value={formData.external_technician_name}
                        onChange={handleChange}
                        placeholder="Name of technician who performed the work"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {!isServiceVisit && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Scheduled Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="scheduled_date"
                          value={formData.scheduled_date}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                        >
                          {RECORD_STATUSES.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>

            {(isServiceVisit || isEdit) && (
              <Card className="mb-4">
                <Card.Header>
                  <i className="fa fa-clipboard me-2"></i>
                  Service Details
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Cost ($)</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          name="cost"
                          value={formData.cost}
                          onChange={handleChange}
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Parts Replaced</Form.Label>
                        <Form.Control
                          type="text"
                          name="parts_replaced"
                          value={formData.parts_replaced}
                          onChange={handleChange}
                          placeholder="List any parts that were replaced"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Findings</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="findings"
                          value={formData.findings}
                          onChange={handleChange}
                          placeholder="What issues or observations were found?"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Recommendations</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="recommendations"
                          value={formData.recommendations}
                          onChange={handleChange}
                          placeholder="Any recommendations for future maintenance?"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </Col>

          <Col md={4}>
            <Card>
              <Card.Body className="d-grid gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-save me-2"></i>
                      {isServiceVisit
                        ? "Record Visit"
                        : isEdit
                        ? "Update Record"
                        : "Schedule Maintenance"}
                    </>
                  )}
                </Button>
                <Link to="/maintenance/records" className="btn btn-outline-secondary">
                  Cancel
                </Link>
              </Card.Body>
            </Card>

            {!isServiceVisit && (
              <Card className="mt-4">
                <Card.Header>
                  <i className="fa fa-lightbulb-o me-2"></i>
                  Tips
                </Card.Header>
                <Card.Body>
                  <ul className="mb-0 ps-3">
                    <li className="mb-2">Schedule maintenance based on manufacturer recommendations</li>
                    <li className="mb-2">Include detailed descriptions for better tracking</li>
                    <li>Assign a service provider if using external contractors</li>
                  </ul>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default MaintenanceRecordForm;
